import { useEffect, useRef } from 'react';
import { FiBriefcase } from 'react-icons/fi';
import { QuoteEstimator } from './QuoteEstimator';

const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

export const HeroSection = ({ profile = {}, stats = [], hasProjects = true }) => {
    const heroRef = useRef(null);

    useEffect(() => {
        const hero = heroRef.current;
        if (!hero) return undefined;

        const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
        const mobileViewport = window.matchMedia('(max-width: 760px)');
        let frameId = null;
        let pointerX = 0;
        let pointerY = 0;

        const resetParallax = () => {
            hero.style.setProperty('--pb-bg-y', '0px');
            hero.style.setProperty('--pb-grid-y', '0px');
            hero.style.setProperty('--pb-color-y', '0px');
            hero.style.setProperty('--pb-streak-y', '0px');
            hero.style.setProperty('--pb-orb-a-y', '0px');
            hero.style.setProperty('--pb-orb-b-y', '0px');
            hero.style.setProperty('--pb-content-y', '0px');
            hero.style.setProperty('--pb-bg-scale', '1.18');
            hero.style.setProperty('--pb-pointer-x', '0px');
            hero.style.setProperty('--pb-pointer-y', '0px');
        };

        const updateParallax = () => {
            frameId = null;

            if (reducedMotion.matches || mobileViewport.matches) {
                resetParallax();
                return;
            }

            const rect = hero.getBoundingClientRect();
            const viewportHeight = window.innerHeight || 1;

            if (rect.bottom < -160 || rect.top > viewportHeight + 160) return;

            // 0 at the top of the page. While the hero leaves the viewport the
            // background is deliberately compensated much more than the content,
            // making the depth easy to perceive instead of merely decorative.
            const scrolled = clamp(-rect.top, 0, rect.height);
            const ratio = clamp(scrolled / viewportHeight, 0, 1.35);

            hero.style.setProperty('--pb-bg-y', `${Math.round(scrolled * 0.62)}px`);
            hero.style.setProperty('--pb-grid-y', `${Math.round(scrolled * 0.18)}px`);
            hero.style.setProperty('--pb-color-y', `${Math.round(scrolled * 0.36)}px`);
            hero.style.setProperty('--pb-streak-y', `${Math.round(scrolled * -0.12)}px`);
            hero.style.setProperty('--pb-orb-a-y', `${Math.round(scrolled * 0.28)}px`);
            hero.style.setProperty('--pb-orb-b-y', `${Math.round(scrolled * 0.12)}px`);
            hero.style.setProperty('--pb-content-y', `${Math.round(scrolled * -0.035)}px`);
            hero.style.setProperty('--pb-bg-scale', String(1.18 + (ratio * 0.045)));
            hero.style.setProperty('--pb-pointer-x', `${Math.round(pointerX * 16)}px`);
            hero.style.setProperty('--pb-pointer-y', `${Math.round(pointerY * 10)}px`);
        };

        const requestUpdate = () => {
            if (frameId !== null) return;
            frameId = window.requestAnimationFrame(updateParallax);
        };

        const onPointerMove = (event) => {
            const rect = hero.getBoundingClientRect();
            if (rect.bottom < 0 || rect.top > window.innerHeight) return;
            pointerX = clamp(((event.clientX - rect.left) / Math.max(rect.width, 1)) * 2 - 1, -1, 1);
            pointerY = clamp(((event.clientY - rect.top) / Math.max(rect.height, 1)) * 2 - 1, -1, 1);
            requestUpdate();
        };

        const onPointerLeave = () => {
            pointerX = 0;
            pointerY = 0;
            requestUpdate();
        };

        updateParallax();
        window.addEventListener('scroll', requestUpdate, { passive: true });
        window.addEventListener('resize', requestUpdate, { passive: true });
        hero.addEventListener('pointermove', onPointerMove, { passive: true });
        hero.addEventListener('pointerleave', onPointerLeave, { passive: true });

        return () => {
            if (frameId !== null) window.cancelAnimationFrame(frameId);
            window.removeEventListener('scroll', requestUpdate);
            window.removeEventListener('resize', requestUpdate);
            hero.removeEventListener('pointermove', onPointerMove);
            hero.removeEventListener('pointerleave', onPointerLeave);
        };
    }, []);

    return (
        <section id="home" className="PB__hero" ref={heroRef}>
            <div className="PB__hero_scene" aria-hidden="true">
                <div className="PB__hero_parallax_media" />
                <div className="PB__hero_depth PB__hero_depth--color" />
                <div className="PB__hero_depth PB__hero_depth--grid" />
                <div className="PB__hero_streak PB__hero_streak--a" />
                <div className="PB__hero_streak PB__hero_streak--b" />
                <div className="PB__hero_depth PB__hero_depth--vignette" />
            </div>

            <div className="PB__hero_orb PB__hero_orb--a" />
            <div className="PB__hero_orb PB__hero_orb--b" />

            <div className="PB__container PB__hero_grid">
                <div className="PB__hero_copy">
                    <span className="PB__eyebrow">{profile.badge || 'Portfolio profesional'}</span>
                    <h1 dangerouslySetInnerHTML={{ __html: profile.hero_title || 'Baila con <em>codigo</em>.' }} />
                    <p>{profile.hero_subtitle || profile.summary || 'Desarrollo soluciones digitales con foco en arquitectura, rendimiento y experiencia.'}</p>
                    {hasProjects && (
                        <div className="PB__hero_actions">
                            <a className="PB__button PB__button--primary" href="#proyectos"><FiBriefcase /> Ver trabajo</a>
                        </div>
                    )}
                    <div className="PB__stats">
                        {stats.slice(0, 4).map((stat) => (
                            <div key={stat.id || stat.label} className="PB__stat"><strong>{stat.value}</strong><span>{stat.label}</span></div>
                        ))}
                    </div>
                </div>

                <QuoteEstimator />
            </div>
        </section>
    );
};
